import React, { useState, useEffect } from 'react';
import { formatDistanceToNow } from 'date-fns';
import api from '../services/api';

function Timeline() {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchActivities();
    const interval = setInterval(fetchActivities, 5000);
    return () => clearInterval(interval);
  }, [filter]);

  const fetchActivities = async () => {
    try {
      const params = filter !== 'all' ? { type: filter } : {};
      const response = await api.get('/api/activities', { params: { ...params, limit: 100 } });
      setActivities(response.data.activities || []);
    } catch (error) {
      console.error('Failed to fetch activities:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSeverityClass = (severity) => {
    switch (severity) {
      case 'critical': return 'severity-critical';
      case 'error': return 'severity-error';
      case 'warning': return 'severity-warning';
      case 'success': return 'severity-success';
      default: return 'severity-info';
    }
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'agent_registered': return '🎯';
      case 'agent_beacon': return '📡';
      case 'task_created': return '📝';
      case 'task_completed': return '✅';
      case 'task_failed': return '❌';
      case 'keylog_started': return '⌨️';
      case 'keylog_stopped': return '🛑';
      case 'screenshot_taken': return '📸';
      case 'file_uploaded': return '📤';
      case 'file_downloaded': return '📥';
      case 'process_killed': return '💀';
      case 'persistence_installed': return '🔗';
      case 'socks_started': return '🔌';
      default: return '📌';
    }
  };

  const activityTypes = [
    { value: 'all', label: 'All Activities' },
    { value: 'agent_registered', label: 'Agent Registrations' },
    { value: 'task_completed', label: 'Completed Tasks' },
    { value: 'task_failed', label: 'Failed Tasks' },
    { value: 'keylog_started', label: 'Keylogger Events' },
    { value: 'persistence_installed', label: 'Persistence' },
    { value: 'process_killed', label: 'Process Kills' },
  ];

  return (
    <div className="page-container">
      <div className="page-header" style={{ marginBottom: '30px' }}>
        <div>
          <h1 className="page-title">📊 Activity Timeline</h1>
          <p className="page-subtitle">Real-time activity feed across all agents</p>
        </div>
      </div>

      <div className="card" style={{ marginBottom: '30px', padding: '20px' }}>
        <div className="filter-row">
          <label style={{ marginRight: '15px', fontWeight: '500' }}>Filter by type:</label>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="form-select"
            style={{ marginLeft: '10px', width: '200px' }}
          >
            {activityTypes.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="timeline-container">
        {loading ? (
          <div className="loading-spinner">Loading...</div>
        ) : activities.length === 0 ? (
          <div className="empty-state">
            <p>No activities recorded yet.</p>
          </div>
        ) : (
          <div className="timeline">
            {activities.map((activity, index) => (
              <div key={activity.activity_id} className={`timeline-item ${getSeverityClass(activity.severity)}`}>
                <div className="timeline-marker">
                  <span className="timeline-icon">{getActivityIcon(activity.activity_type)}</span>
                </div>
                <div className="timeline-content">
                  <div className="timeline-header">
                    <span className="timeline-title">{activity.title}</span>
                    <span className="timeline-time">
                      {activity.timestamp && formatDistanceToNow(new Date(activity.timestamp), { addSuffix: true })}
                    </span>
                  </div>
                  {activity.description && (
                    <p className="timeline-description">{activity.description}</p>
                  )}
                  {activity.agent_hostname && (
                    <span className="timeline-agent">
                      Agent: <code>{activity.agent_hostname}</code>
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <style>{`
        .timeline-container {
          padding: 0 20px;
        }

        .timeline {
          position: relative;
          padding-left: 30px;
        }

        .timeline::before {
          content: '';
          position: absolute;
          left: 15px;
          top: 0;
          bottom: 0;
          width: 2px;
          background: linear-gradient(to bottom, var(--neon-green), transparent);
        }

        .timeline-item {
          position: relative;
          padding: 15px 20px;
          margin-bottom: 15px;
          background: var(--bg-secondary);
          border-radius: 8px;
          border-left: 3px solid var(--neon-green);
        }

        .timeline-item.severity-critical {
          border-left-color: #ff4444;
          background: rgba(255, 68, 68, 0.1);
        }

        .timeline-item.severity-error {
          border-left-color: #ff6b6b;
        }

        .timeline-item.severity-warning {
          border-left-color: #ffaa00;
        }

        .timeline-item.severity-success {
          border-left-color: #00ff88;
        }

        .timeline-marker {
          position: absolute;
          left: -38px;
          top: 20px;
          width: 24px;
          height: 24px;
          background: var(--bg-primary);
          border: 2px solid var(--neon-green);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .timeline-icon {
          font-size: 12px;
        }

        .timeline-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
        }

        .timeline-title {
          font-weight: 600;
          color: var(--text-primary);
        }

        .timeline-time {
          font-size: 12px;
          color: var(--text-muted);
        }

        .timeline-description {
          color: var(--text-secondary);
          font-size: 14px;
          margin: 8px 0;
        }

        .timeline-agent {
          display: inline-block;
          font-size: 12px;
          padding: 2px 8px;
          background: rgba(255, 34, 68, 0.2);
          border-radius: 4px;
          color: var(--neon-cyan);
        }

        .timeline-agent code {
          color: var(--neon-green);
        }

        .filter-row {
          display: flex;
          align-items: center;
        }

        .empty-state {
          text-align: center;
          padding: 60px;
          color: var(--text-muted);
        }
      `}</style>
    </div>
  );
}

export default Timeline;
